import { BaseLayer } from '../layers/BaseLayer.js';
import { Spiral, SpiralPoint } from '../utils/spiral.js';
import { drawTiled } from '../utils/seamless.js';
import { easeOutCirc } from '../utils/easings.js';

/********************************/
/*   HAND SPIRAL LAYER CLASS    */
/********************************/

/**
 * Hand spiral layer - renders an outward spiral of hand images in front of the gem
 */
export class HandSpiralLayer extends BaseLayer {
  constructor(width, height, density, handImage) {
    super(width, height, density, true);
    this.handImage = handImage;
  }

  setup() {
    // Scale factor based on buffer size (use smaller dimension for consistency)
    const scale = Math.min(this.width, this.height);

    // Spiral parameters (all scaled relative to buffer size)
    const numHands = 18; // Number of hand instances in the spiral
    const minRadius = scale * 0.15; // 15% of buffer size
    const maxRadius = scale * 0.7; // 50% of buffer size

    // Calculate size (hands get smaller towards center, scaled to buffer size)
    const minHandSize = scale * 0.1; // 10% of buffer size (at center)
    const maxHandSize = scale * 0.4; // 40% of buffer size (at edge)

    // Note: HandSpiral grows outward (opposite of CloudSpiral)
    // We invert the spiral by using negative spiralTurns
    this.spiral = new Spiral(numHands, maxRadius - minRadius, -2);
    this.minRadius = minRadius;

    // Create spiral points with hand-specific properties
    this.spiralPoints = [];
    for (let i = 0; i < numHands; i++) {
      const t = i / numHands;

      // Calculate hand size (shrinks towards center: t=0 is outer, t=1 is inner)
      // Reverse the mapping so larger hands are at the edge (t=0) and smaller at center (t=1)
      const handSize = maxHandSize - t * (maxHandSize - minHandSize);

      this.spiralPoints.push(new SpiralPoint(this.spiral, i, {
        size: handSize,
        t: t
      }));
    }
  }

  draw() {
    // Clear the overlay (transparent background)
    clear();

    // Spiral rotates based on animation loop
    const rotationSpeed = this.loopPercent * 0.09;

    imageMode(CENTER);

    // Draw hands from innermost to outermost
    for (let i = 0; i < this.spiralPoints.length; i++) {
      const state = this.spiralPoints[i].getState(rotationSpeed);

      const angle = state.angle + PI * 0.18;

      // Adjust position to account for minRadius offset
      const x = state.x + cos(angle) * this.minRadius;
      const y = state.y + sin(angle) * this.minRadius;
      // Fade out towards center using easing
      const easedT = easeOutCirc(state.t);
      const alpha = map(easedT, 1, 0, 200, 255);

      // Draw hand
      push();
      translate(x, y);
      rotate(state.angle); // Hands point along spiral
      tint(255, alpha);
      image(this.handImage, 0, 0, state.size, state.size);
      pop();
    }

    noTint();
  }
}
